import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Calendar, Clock, User, CreditCard, Check } from 'lucide-react';
import { parseLocalDate } from '../lib/utils';

const API = '/api';

const PayBalance = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bookings/${bookingId}/balance`);
      setBooking(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching booking:', error);
      setError(error.response?.data?.detail || 'Failed to load booking details');
      setLoading(false);
    }
  };

  const handlePayBalance = async () => {
    try {
      setProcessing(true);
      const response = await axios.post(`${API}/checkout/balance-payment`, {
        booking_id: bookingId
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 flex items-center justify-center p-4">
        <div className="bg-red-900/20 border border-red-500/30 rounded-xl p-8 max-w-md text-center">
          <div className="text-red-400 text-5xl mb-4">⚠️</div>
          <h1 className="text-white text-2xl font-bold mb-2">Error</h1>
          <p className="text-gray-300">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  const balanceDue = booking.price - (booking.deposit || 0);
  const isPaid = booking.payment_status === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-blue-900 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto mt-8 sm:mt-12">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Session Balance Payment</h1>
          <p className="text-gray-300">Complete your booking payment</p>
        </div>

        {/* Booking Details Card */}
        <div className="bg-gradient-to-br from-purple-900/40 to-blue-900/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6 sm:p-8 mb-6">
          <div className="space-y-4">
            {/* Booking Number */}
            {booking.booking_number && (
              <div className="text-center pb-4 border-b border-purple-500/20">
                <div className="text-sm text-gray-400 mb-1">Booking Reference</div>
                <div className="text-2xl font-mono font-bold text-purple-400">{booking.booking_number}</div>
              </div>
            )}

            {/* Session Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="text-purple-400" size={20} />
                <div>
                  <div className="text-gray-400 text-sm">Date</div>
                  <div className="text-white font-semibold">
                    {parseLocalDate(booking.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="text-purple-400" size={20} />
                <div>
                  <div className="text-gray-400 text-sm">Time</div>
                  <div className="text-white font-semibold">{booking.time}</div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <User className="text-purple-400" size={20} />
              <div>
                <div className="text-gray-400 text-sm">Client</div>
                <div className="text-white font-semibold">{booking.name}</div>
              </div>
            </div>

            <div className="bg-black/30 rounded-lg p-4">
              <div className="text-gray-400 text-sm mb-1">Service</div>
              <div className="text-white font-semibold">{booking.service_name}</div>
              <div className="text-gray-400 text-sm mt-1">with {booking.engineer_name}</div>
            </div>
          </div>
        </div>

        {/* Payment Card */}
        <div className="bg-gradient-to-br from-green-900/20 to-blue-900/20 backdrop-blur-sm border border-green-500/30 rounded-2xl p-6 sm:p-8">
          {isPaid ? (
            <div className="text-center">
              <div className="text-green-400 text-6xl mb-4">
                <Check size={64} className="mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Complete!</h2>
              <p className="text-gray-300">Your session is fully paid. See you soon!</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center gap-3 mb-6">
                <DollarSign className="text-green-400" size={32} />
                <h2 className="text-2xl font-bold text-white">Balance Due</h2>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Session Total</span>
                  <span className="text-white font-semibold">${booking.price.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Deposit Paid</span>
                  <span className="text-green-400 font-semibold">-${(booking.deposit || 0).toFixed(2)}</span>
                </div>

                <div className="border-t border-green-500/20 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold text-xl">Amount Due</span>
                    <span className="text-green-400 font-bold text-3xl">${balanceDue.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePayBalance}
                disabled={processing}
                className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard size={24} />
                    <span>Pay Balance Now</span>
                  </>
                )}
              </button>

              <p className="text-center text-gray-400 text-sm mt-4">
                Secure payment powered by Stripe
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayBalance;
